<template>
  <div class="flex flex-col items-center w-full pb-12">
    <div
      v-if="noneAvailable"
      class="absolute z-10 flex p-4 text-sm bg-blue-100 shadow-md book-a-meeting w-84 rounded-md transform top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
    >
      <InformationCircleIcon class="mt-px mr-4 text-blue-700" size="1.25x" />
      <div>
        <p class="mb-0">There are no available times</p>
        <NuxtLink
          v-if="company && isAskAQuestionEnabled"
          class="flex items-center mt-4 text-gray-900 cursor-pointer"
          data-testid="choose-time-ask-a-question"
          :to="{ name: 'companies-id', params: { id: company.id }, query: { aaqOpen: 'true' } }"
          >Ask a Question instead
          <ArrowRightIcon class="flex-shrink-0 ml-2" size="1x" />
        </NuxtLink>
      </div>
    </div>

    <div id="cronofy-date-time-picker" :class="{ 'opacity-50': noneAvailable }" />
    <div class="mt-12 text-center">
      <VLink
        class="cursor-pointer text-copper-700"
        data-testid="open-timezone-modal-btn"
        component="button"
        type="button"
        font-weight="normal"
        @click="isTimeZoneOpen = true"
        >{{ getExtendedTimeZoneName(timeZone) }}</VLink
      >
    </div>

    <VModal :showing="isTimeZoneOpen" @close="isTimeZoneOpen = false">
      <h2 class="text-lg">Timezone</h2>
      <select
        v-model="timeZone"
        class="block w-full max-w-md px-3 py-2 text-sm text-gray-700 border border-gray-300 appearance-none rounded-md focus_outline-none focus_ring-2 focus_ring-gray-900 focus_border-gray-900"
        data-testid="timezone-select"
        name="timezone"
      >
        <option v-for="tz in timeZones" :key="tz.value" :value="tz.value">{{ tz.name }}</option>
      </select>
      <div class="mt-8 text-right">
        <VButton class="ml-4" size="sm" variant="copper" data-testid="timezone-modal-close-btn" @click="isTimeZoneOpen = false"
          >Close</VButton
        >
      </div>
    </VModal>
  </div>
</template>

<script lang="ts" setup>
import { DateTime } from 'luxon'
import type { Ref, PropType } from 'vue'
import { InformationCircleIcon, ArrowRightIcon } from '@heroicons/vue/24/solid'

import type { Company } from '@/entities/company'
import { DateTimePicker } from 'cronofy-elements'
import TIME_ZONES from '@/lib/constants/timezones'
import { getExtendedTimeZoneName } from '@/lib/time-zones-utils'

const props = defineProps({
  duration: {
    type: Number,
    required: true,
  },
  token: {
    type: String,
    required: true,
  },
  subs: {
    type: Array as PropType<Readonly<string[]>>,
    required: true,
  },
  company: {
    type: Object as PropType<Company>,
    required: true,
  },
  isAskAQuestionEnabled: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['choose-time'])

const route = useRoute()
const { $analytics } = useNuxtApp()
const NuxtLink = resolveComponent('NuxtLink')

const timeZone = ref(Intl.DateTimeFormat().resolvedOptions().timeZone)
const isTimeZoneOpen = ref(false)
const timeZones: Ref<{ name: string; value: string }[]> = computed(() =>
  TIME_ZONES.map((tz: string) => ({ name: getExtendedTimeZoneName(tz), value: tz }))
)
const noneAvailable = ref(false)
let picker: DateTimePicker | undefined

const pickerOpts = computed(() => ({
  data_center: 'ca',
  element_token: props.token,
  target_id: 'cronofy-date-time-picker',
  tzid: timeZone.value,
  availability_query: {
    query_periods: [
      {
        start: DateTime.utc().startOf('second').toISO(), // cronofy HATES milliseconds
        end: DateTime.utc().startOf('day').plus({ days: 35 }).toISO(),
      },
    ],
    participants: [
      {
        members: props.subs.map((sub: string) => ({ sub, managed_availability: true })),
        required: 1,
      },
    ],
    required_duration: { minutes: props.duration },
  },
  styles: {
    prefix: 'VRIFY',
    colors: {
      neutralDark: '#222222', // gray-900
      neutralLight: '#CACACA', // gray-400
      button: '#D3EFEC',
      buttonConfirm: '#2f5a62', // copper-800
      buttonActiveText: '#ffffff',
      buttonTextHover: '#ffffff',
      buttonHover: '#2f5a62',
      buttonActive: '#2f5a62',
    },
  },
  callback({ notification }: any) {
    if (notification.type == 'slot_selected') {
      const startDateTime = DateTime.fromISO(notification.slot.start, { zone: notification.tzid })
      $analytics.track('bamInvestorTimeSelected', {
        time: startDateTime,
        request_setting_id: route.params.id,
        company_id: props.company.id,
      })
      emit('choose-time', startDateTime)
    } else if (notification.type == 'no_slots_found') {
      $analytics.track('bamInvestorNoTimeSlotsAvailable', {
        request_setting_id: route.params.id,
        company_id: props.company.id,
      })

      noneAvailable.value = true
    }
  },
}))

watch(timeZone, () => {
  picker?.update(pickerOpts.value as any)
})

onMounted(async () => {
  picker = DateTimePicker(pickerOpts.value as any)
})
</script>

<style lang="stylus">
.theme-button
  @apply border rounded-md outline-none font-inter transition-all duration-300 cursor-pointer shadow-sm m-auto my-3 w-full sm_w-80 xl_w-96 font-semibold px-4 py-2 xl_px-6 xl_py-3 outline-none rounded-full
  font-size 1.125rem

@screen md
  .VRIFY.DTP .VRIFY__
    &column
      &--left, &--right
        @apply px-0
    &wrapper
      @apply gap-0
    &time-slots-group--wrapper
      position relative
      min-height 26rem
      max-height calc(100vh - 27rem)
      overflow-y auto
      &:after
        z-index 1
        content ''
        display block
        position sticky
        bottom 0
        left 0
        right 0
        height 3rem
        background linear-gradient(0deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%)

.VRIFY.DTP .VRIFY__
  @apply font-inter
  &details
    display none
  &wrapper
    @apply gap-6
  &column
    &--left, &--right
      @apply px-0
  &calendar
    &-header
      @apply flex items-center mt-2 font-semibold
      &--title
        @apply order-1 w-auto my-0 mr-4
      &-button
        @apply order-2 w-auto px-2 mb-1 py-0
        &:hover
          @apply shadow-none
        &:last-child
          @apply order-3 flex-grow w-auto text-left px-2
        &--icon
          @apply w-4 h-4
    &-grid
      &--day
        @apply p-2
      &--button
        @apply text-gray-400 font-semibold
        padding-top 0.6rem
        padding-bottom 0.6rem
        width 2.75rem
        height 2.75rem
        font-size 1.125rem
        &:focus
          outline 0
      &--prevnext
        @apply text-gray-200
      &--available, &--selected
        @apply font-semibold
      &--available
        @apply rounded-full border-transparent text-gray-700
        &:hover
          @apply border-transparent shadow-none
      &--selected
        @apply text-white
      &--week-day, &--week-day:first-child
        @apply font-inter uppercase font-medium
  &time-slots-list--header, &confirm-details--info
    @apply font-semibold
  &time-slot, &confirm-button, &cancel-button
    @extend .theme-button
  &time-slot
    @apply border-copper-200 text-gray-700 bg-copper-200 shadow-none
    &:hover, &:focus
      @apply border-copper-200 text-gray-700 bg-copper-200 shadow-none outline-none
  &slot-list--item
    &:first-child button
      @apply mt-0
    &:last-child button
      @apply mb-0
  &confirm-button
    @apply border-copper-800 text-white bg-copper-800
    &:focus
      @apply border-copper-800 text-white bg-copper-800 shadow-none
    &:hover
      @apply border-copper-500 text-white bg-copper-500 shadow-none
  &cancel-button
    @apply border-gray-300 text-gray-900 bg-white
    &:hover, &:focus
      @apply border-gray-900 text-gray-900 bg-gray-50 shadow-none outline-none
</style>
